import Dexie, { Transaction, TransactionMode } from 'dexie'

import { logError } from '@/shared/logger'
import { UPSettingsSchema, DexieModelsGlobal } from './UPSettingsSchema'
import { SETTINGS_DB_NAME } from '@/shared/config'
import { ChainIdHex, NetworkId } from '@/shared/models/Network'
import { NetworkInfoDBSchema, RelayServiceDBSchema } from './models'

export class UPSettingsDatabase {
  private schema: UPSettingsSchema

  static async load(): Promise<UPSettingsDatabase> {
    const schema = new UPSettingsSchema(SETTINGS_DB_NAME)
    try {
      await schema.open()
    } catch (error) {
      logError('Error loading network config database', error)
    }
    return new UPSettingsDatabase(schema)
  }

  constructor(dexieDatabase: UPSettingsSchema) {
    this.schema = dexieDatabase
  }

  private getTable = <T extends keyof DexieModelsGlobal>(
    name: T
  ): Dexie.Table<any, any> => {
    return this.schema[name]
  }

  put = async <T extends keyof DexieModelsGlobal>(
    prop: T,
    obj: DexieModelsGlobal[T],
    key?: any
  ): Promise<void> => {
    await this.getTable(prop).put(obj, key)
  }

  bulkPut = async <T extends keyof DexieModelsGlobal>(
    prop: T,
    objects: DexieModelsGlobal[T][]
  ): Promise<void> => {
    await this.getTable(prop).bulkPut(objects)
  }

  add = async <T extends keyof DexieModelsGlobal>(
    prop: T,
    obj: DexieModelsGlobal[T]
  ): Promise<void> => {
    await this.getTable(prop).add(obj)
  }

  clear = async <T extends keyof DexieModelsGlobal>(prop: T): Promise<void> => {
    await this.getTable(prop).clear()
  }

  clearAll = async (): Promise<void> => {
    await this.schema.networks.clear()
    await this.schema.relayers.clear()
  }

  transaction = async <
    T extends keyof DexieModelsGlobal,
    K extends keyof DexieModelsGlobal,
    U,
  >(
    mode: TransactionMode,
    table1: T,
    table2: K,
    scope: (trans: Transaction) => Promise<U>
  ) => {
    return await this.schema.transaction(
      mode,
      this.getTable(table1),
      this.getTable(table2),
      scope
    )
  }

  // ------------------------ Networks ------------------------

  async getNetworks(): Promise<NetworkInfoDBSchema[]> {
    return this.schema.networks.toArray()
  }

  async getNetwork(id: NetworkId): Promise<NetworkInfoDBSchema | undefined> {
    return await this.schema.networks.get(id)
  }

  async getNetworkByChainId(
    chainId: ChainIdHex
  ): Promise<NetworkInfoDBSchema | undefined> {
    return await this.schema.networks
      .where('chainId')
      .equalsIgnoreCase(chainId)
      .first()
  }

  async removeNetwork(id: NetworkId): Promise<void> {
    return await this.schema.networks.delete(id)
  }

  // ------------------------ Relayers ------------------------

  async getUserRelayers(chainId: string): Promise<RelayServiceDBSchema[]> {
    return await this.schema.relayers
      .filter(r => !r.isCustom || r.isCustom)
      .filter(r =>
        r.chainIds.some(c => c.toLowerCase() === chainId.toLowerCase())
      )
      .sortBy('isCustom')
  }

  async getRelayerById(id: string): Promise<RelayServiceDBSchema | undefined> {
    return await this.schema.relayers.get(id)
  }

  async getRelayerByName(
    name: string
  ): Promise<RelayServiceDBSchema | undefined> {
    return await this.schema.relayers.where('name').equals(name).first()
  }

  async getRelayerByApiUrl(
    apiUrl: string
  ): Promise<RelayServiceDBSchema | undefined> {
    return await this.schema.relayers.where('apiUrl').equals(apiUrl).first()
  }

  async getAllRelayers(): Promise<RelayServiceDBSchema[]> {
    return await this.schema.relayers.orderBy('createdAt').reverse().toArray()
  }

  async getRelayersByChainId(chainId: string): Promise<RelayServiceDBSchema[]> {
    return await this.schema.relayers
      .where('chainIds')
      .equalsIgnoreCase(chainId)
      .toArray()
  }

  async updateRelayerChainIds(
    relayerId: string,
    chainIds: ChainIdHex[]
  ): Promise<void> {
    await this.schema.relayers.update(relayerId, {
      chainIds: chainIds,
    })
  }

  async removeRelayer(id: string): Promise<void> {
    return await this.schema.relayers.delete(id)
  }
}
